import { CheckCircle2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { SandboxClinicalDataContext } from '../../../../contexts/sandboxClinicalData.context';
import logoDark from '../../../../images/logo-dark.png';
import logo from '../../../../images/logo.png';
import { SANDBOX_PATIENT } from './fixtures/anitaBack';
import clinicalData from './fixtures/publicClinicalData';
import SoapSandbox from './SoapSandbox';

/** Anonymous entry point. Its own query cache cannot inherit a signed-in clinic's data. */
const PublicSoapSandbox = () => {
  useEffect(() => {
    // The app shell scrolls its own panes and locks the body in index.html.
    // This public page uses document scrolling, including its step resets.
    const body = document.body;
    const wasScrollLocked = body.classList.contains('overflow-hidden');
    body.classList.remove('overflow-hidden');

    return () => {
      if (wasScrollLocked) body.classList.add('overflow-hidden');
    };
  }, []);

  const [started, setStarted] = useState(false);
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { staleTime: Infinity, retry: false, refetchOnWindowFocus: false },
        },
      })
  );
  const brand = (
    <a href="https://chiroup.com" aria-label="ChiroUp homepage" className="inline-flex">
      <img src={logo} alt="ChiroUp" className="h-12 w-auto dark:hidden" />
      <img src={logoDark} alt="ChiroUp" className="hidden h-12 w-auto dark:block" />
    </a>
  );

  return (
    <QueryClientProvider client={queryClient}>
      <SandboxClinicalDataContext.Provider value={clinicalData}>
        <main className="isolate min-h-dvh bg-gray-50 text-gray-900 antialiased dark:bg-dark-gray-900 dark:text-gray-100">
          {started ? (
            <>
              <header className="border-b border-gray-950/10 bg-white py-4 dark:border-white/10 dark:bg-dark-gray-900">
                <div className="mx-auto max-w-6xl px-4 sm:px-6">{brand}</div>
              </header>
              <SoapSandbox
                onExit={() => {
                  window.scrollTo({ top: 0 });
                  setStarted(false);
                }}
              />
            </>
          ) : (
            <section className="py-6 sm:py-12">
              <div className="mx-auto max-w-5xl px-4 sm:px-6">
                <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-950/10 sm:p-10 dark:bg-dark-gray-800 dark:shadow-none dark:ring-white/10">
                  <header className="mb-8 text-center">{brand}</header>
                  <div className="grid items-center gap-8 md:grid-cols-[2fr_3fr]">
                    <img
                      src={SANDBOX_PATIENT.photo}
                      alt=""
                      className="aspect-square w-full rounded-xl object-cover object-top"
                    />
                    <div className="flex min-w-0 flex-col items-start gap-4">
                      <p className="font-semibold text-primary-600 dark:text-primary-300">
                        Interactive Demo
                      </p>
                      <h1 className="max-w-[30ch] text-balance text-3xl font-semibold tracking-tight sm:text-4xl">
                        Meet Your New Patient
                      </h1>
                      <h2 className="text-balance text-2xl font-medium tracking-tight">
                        Anita Back
                      </h2>
                      <ul role="list" className="flex w-full flex-col gap-3 text-base sm:text-sm">
                        <li className="flex items-start gap-3 rounded-lg border border-gray-950/10 p-3 dark:border-white/10">
                          <CheckCircle2
                            className="size-6 shrink-0 stroke-primary-600 dark:stroke-primary-300"
                            aria-hidden
                          />
                          <p>
                            Chief complaint:{' '}
                            <strong className="font-semibold text-primary-700 dark:text-primary-300">
                              Low back and right hip pain
                            </strong>
                          </p>
                        </li>
                        <li className="flex items-start gap-3 rounded-lg border border-gray-950/10 p-3 dark:border-white/10">
                          <CheckCircle2
                            className="size-6 shrink-0 stroke-primary-600 dark:stroke-primary-300"
                            aria-hidden
                          />
                          <p>Appointment scheduled</p>
                        </li>
                        <li className="flex items-start gap-3 rounded-lg border border-gray-950/10 p-3 dark:border-white/10">
                          <CheckCircle2
                            className="size-6 shrink-0 stroke-primary-600 dark:stroke-primary-300"
                            aria-hidden
                          />
                          <p>Intake surveys texted and completed</p>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="mt-8 flex flex-col items-center gap-5 border-t border-gray-950/10 pt-6 text-center dark:border-white/10">
                    <p className="max-w-[56ch] text-pretty text-base text-gray-600 dark:text-gray-300">
                      Review her history, select diagnoses, document findings, and complete your
                      first-visit SOAP note.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        window.scrollTo({ top: 0 });
                        setStarted(true);
                      }}
                      className="w-full rounded-lg bg-primary-600 px-4 py-3 font-semibold text-white hover:bg-primary-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 sm:max-w-md"
                    >
                      Start Walkthrough
                    </button>
                    <p className="max-w-[56ch] text-pretty text-base text-gray-500 sm:text-sm dark:text-gray-400">
                      No account needed. This sample encounter stays in your browser and nothing is
                      saved.
                    </p>
                  </div>
                </div>
              </div>
            </section>
          )}
        </main>
      </SandboxClinicalDataContext.Provider>
    </QueryClientProvider>
  );
};

export default PublicSoapSandbox;
